// #region Imports
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ConfirmResponse,
  useConfirmDialog
} from '_shared/_providers/dialogs/confirm/confirm-dialog';
import { useDialog } from '_shared/_providers/dialogs/dialog';
import { RootState, useAttorneyHubDispatch } from '_shared/_stores/redux-store';
import { MRTNoMemo } from '_shared/_views/tables/table';
import Guid from '_shared/guids/guid';
import CompanyAvatar from 'marketplace/_views/avatars/company-avatar';
import {
  useDeleteCompanyBookmarkMutation,
  useGetAllCompanyBookmarksQuery,
} from 'marketplace/bookmarks/_api/bookmark-api';
import { PageTab } from 'marketplace/bookmarks/_views/bookmarks-page-tabs';
import BookmarkedCompany from 'marketplace/bookmarks/company-bookmarks/bookmarked-company';
import {
  getCompaniesByIds,
  populateCompaniesByIds
} from 'marketplace/companies/_store/companies-redux-slice';
import Company from 'marketplace/companies/company';
import ViewCompanyProfile from 'marketplace/companies/profiles/_views/view-company-profile';
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton
} from "material-react-table";
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
// #endregion

// #region Component Input Props
type CompanyBookmarksProps = {
  activeTab: PageTab;
};

const columnHelper = createMRTColumnHelper<BookmarkedCompany>();

export default function CompanyBookmarks(props: Readonly<CompanyBookmarksProps>) {
  const { activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Bookmark Query
  const { data: bookmarksJson, isLoading: bookmarksLoading, isError: bookmarksError } = useGetAllCompanyBookmarksQuery();
  const [deleteCompanyBookmark, { isLoading: isDeleting }] = useDeleteCompanyBookmarkMutation();
  const bookmarks = useMemo(
    () => bookmarksJson?.map((b) => BookmarkedCompany.fromJSON(b)).filter((b): b is BookmarkedCompany => !!b) ?? [],
    [bookmarksJson],
  );

  // All companyIds to fetch
  const bookmarkedCompanyIds = useMemo(
    () => bookmarks.map(b => b.companyId),
    [bookmarks]
  );

  // Company Selectors
  const companies = useSelector(getCompaniesByIds(bookmarkedCompanyIds));
  const companyEntries = useSelector((s: RootState) => s.companies.byId.entries);
  const loadingById = useSelector((s: RootState) => s.companies.byId.loading);
  const errorById = useSelector((s: RootState) => s.companies.byId.error);

  const companiesById = useMemo(() => {
    const map: Record<string, Company> = {};
    for (const company of companies) {
      map[company.id.value] = company;
    }
    return map;
  }, [companies]);

  // #region Lifecycle Hooks
  useEffect(() => {
    if (!bookmarkedCompanyIds.length) return;

    const missingIds = bookmarkedCompanyIds.filter((id) => {
      if (companyEntries[id.value]) return false;
      if (loadingById[id.value]) return false;
      return true;
    });

    if (!missingIds.length) return;

    dispatch(populateCompaniesByIds({ ids: missingIds }));
  }, [
    dispatch,
    bookmarkedCompanyIds,
    companyEntries,
    loadingById
  ]);

  useEffect(() => {
    if (bookmarksError) {
      enqueueSnackbar("Error loading bookmarks. Please try again.", { variant: 'error' });
    }
  }, [bookmarksError]);

  // #region Event Handlers
  async function handleDeleteBookmark(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    bookmark: BookmarkedCompany
  ) {
    event.stopPropagation();

    const response = await confirm({
      title: "Delete Bookmark?",
      message: "Do you want to delete?",
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteCompanyBookmark({ bookmarkId: bookmark.id.value }).unwrap();
      enqueueSnackbar("Deleted bookmark", { variant: 'success' });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't delete bookmark. Please try again", { variant: "error" });
    }
  }

  function handleViewProfile(profileId?: Guid): void {
    if (!profileId) return;
    openDialog({
      component: <ViewCompanyProfile companyId={profileId} />,
      titleStyle: {
        paddingBottom: 0
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: false,
      },
    });
  }
  // #endregion

  // #region Columns
  const renderNameColumn = useCallback((bookmark: BookmarkedCompany) => {
    const id = bookmark.companyId;
    const company = companiesById[id.value];

    const isLoading = !!loadingById[id.value];
    const hasError = !!errorById[id.value];

    const loadingText = isLoading ? "Loading..." : "Unknown Company";

    const name =
      company?.profile?.name ??
      (hasError ? "Error loading" : loadingText);

    return (
      <NameCellContainer>
        <CompanyAvatar 
          company={company ?? null}
        />
        <RowTitle>{name}</RowTitle>
      </NameCellContainer>
    );
  }, [
    companiesById,
    loadingById,
    errorById
  ]);

  const columns = useMemo(() => [
    columnHelper.display({
      header: "Name",
      id: "name",
      Cell: ({ row }) => renderNameColumn(row.original)
    }),
    columnHelper.accessor((bookmark) => bookmark.createdDate.format("MM/DD/YY"), {
      header: "Bookmarked On",
      id: "created",
    }),
  ], [renderNameColumn]);
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedCompany> = {
    columns,
    data: bookmarks ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    enableColumnPinning: true,
    enableRowActions: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewProfile(row.original.companyId),
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Bookmarks</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/marketplace/bookmarks/${newValue}`)}>
              <Tab value={PageTab.Individual} label="Individual" />
              <Tab value={PageTab.Team} label="Team" />
              <Tab value={PageTab.Company} label="Company" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? row.companyId?.value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    renderRowActions: ({ row }) => (
      <Tooltip title="Delete Bookmark">
        <IconButton onClick={(event) => handleDeleteBookmark(event, row.original)}>
          <DeleteIcon color="error" />
        </IconButton>
      </Tooltip>
    ),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column"
      },
    },
    muiToolbarAlertBannerProps: !bookmarksError
        ? undefined
        : {
            color: "error",
            children: "Error loading data",
          },
    // onColumnFiltersChange: setColumnFilters,
    // onGlobalFilterChange: setGlobalFilter,
    // onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount: bookmarks?.length ?? 0,
    state: {
      // columnFilters,
      // globalFilter,
      isLoading: bookmarksLoading || isDeleting,
      // pagination,
      showAlertBanner: bookmarksError
        || bookmarkedCompanyIds.some(companyId => !!errorById[companyId.value]),
      showProgressBars: bookmarksLoading || isDeleting,
      // sorting,
    },
  }

  return <MRTNoMemo<BookmarkedCompany> options={tableOptions} />;
}
